import React from 'react';
import { Link } from 'react-scroll';
import Card from '../../atoms/Card';
import { useSelector } from 'react-redux';
import { AppState } from '../../../redux/modules/store';
import { ActionInfoDetailState, NegotiationInfoDetailState, ProductInfoDetailState } from '../../../redux/reducers/CustomerInfoAllReducer';
import { ACTION_TAG, COMMODITY_CATEGORY_TAG } from '../../../Common';

export interface PreviewSideNavigationInfoProps {
  scrollContainerId: string;
}

export interface HeadingInfoProps {
  id: string;
  scrollAnchorId: string;
  headingWord: string;
  childHeadingInfoList?: HeadingInfoProps[];
}

const PreviewSideNavigation: React.FC<PreviewSideNavigationInfoProps> = ({ scrollContainerId }) => {
  const defaultHeadingInfoList: HeadingInfoProps[] = [
    {
      id: 'customerInfo',
      scrollAnchorId: 'customerInfo',
      headingWord: 'お客さま情報',
    },
  ];
  const customerInfoAll = useSelector((state: AppState) => state.customerInfoAll);

  const createProductHeadingList = (productInfoList: ProductInfoDetailState[], negotiationScrollAnchorId: string): HeadingInfoProps[] => {
    return [...productInfoList]
      .sort((a, b) => a.productId - b.productId)
      .map((productInfo, index) => {
        return {
          id: `product${1 + index}`,
          scrollAnchorId: `${negotiationScrollAnchorId}product${productInfo.productId}`,
          headingWord: `商品${index + 1} - ${COMMODITY_CATEGORY_TAG[productInfo.commodityCategoryType].name}`,
        } as HeadingInfoProps;
      });
  };

  const createActionHeadingList = (actionInfoList: ActionInfoDetailState[], negotiationScrollAnchorId: string): HeadingInfoProps[] => {
    return [...actionInfoList]
      .sort((a, b) => a.actionId - b.actionId)
      .map((actionInfo, index) => {
        return {
          id: `action${1 + index}`,
          scrollAnchorId: `${negotiationScrollAnchorId}action${actionInfo.actionId}`,
          headingWord: `アクション${index + 1} - ${ACTION_TAG[actionInfo.actionType].name}`,
        } as HeadingInfoProps;
      });
  };

  const createNegotiationHeadingList = (negotiationInfoDetailList: NegotiationInfoDetailState[]): HeadingInfoProps[] => {
    return [...negotiationInfoDetailList]
      .sort((a, b) => a.negotiationId - b.negotiationId)
      .map((negotiationInfoDetail, index) => {
        const negotiationScrollAnchorId = `negotiation${negotiationInfoDetail.negotiationId}`;
        const childHeadingInfoList: HeadingInfoProps[] = [
          {
            id: `negotiationInfo${1 + index}`,
            scrollAnchorId: `negotiationInfo${negotiationInfoDetail.negotiationId}`,
            headingWord: '商談情報',
          },
        ];
        if (negotiationInfoDetail.productInfoDetail.length !== 0) {
          for (const item of createProductHeadingList(negotiationInfoDetail.productInfoDetail, negotiationScrollAnchorId)) {
            childHeadingInfoList.push(item);
          }
        }
        if (negotiationInfoDetail.actionInfoDetail.length !== 0) {
          for (const item of createActionHeadingList(negotiationInfoDetail.actionInfoDetail, negotiationScrollAnchorId)) {
            childHeadingInfoList.push(item);
          }
        }
        return {
          id: `negotiation${1 + index}`,
          scrollAnchorId: negotiationScrollAnchorId,
          headingWord: negotiationInfoDetail.negotiationName,
          childHeadingInfoList: childHeadingInfoList,
        } as HeadingInfoProps;
      });
  };

  const headingInfoList: HeadingInfoProps[] = !customerInfoAll.negotiationInfoDetail.length
    ? defaultHeadingInfoList
    : defaultHeadingInfoList.concat(createNegotiationHeadingList(customerInfoAll.negotiationInfoDetail));

  return (
    <Card isNoShadow className='preview-side-nav'>
      <Card.Content>
        <ul className='toc'>
          {headingInfoList.map((headingInfo: HeadingInfoProps) => {
            return (
              <li key={headingInfo.id} className='toc__sub'>
                <div className='preview-side-nav__sub-title'>
                  <Link
                    containerId={scrollContainerId}
                    activeClass='active'
                    to={headingInfo.scrollAnchorId}
                    spy={true}
                    smooth='easeInOutQuint'
                    delay={100}
                    duration={600}
                    offset={0}
                    data-testid='preview-side-navi-header-link'
                    data-testid-e2e='preview-side-navi-header-link'>
                    {headingInfo.headingWord}
                  </Link>
                </div>
                {headingInfo.childHeadingInfoList && (
                  <div className='toc__sub-content'>
                    <ul className='toc__sub-nav'>
                      {headingInfo.childHeadingInfoList.map((childHeadingInfo: HeadingInfoProps) => {
                        return (
                          <li key={childHeadingInfo.id} className='toc-item'>
                            <Link
                              containerId={scrollContainerId}
                              activeClass='active'
                              to={childHeadingInfo.scrollAnchorId}
                              spy={true}
                              smooth='easeInOutQuint'
                              delay={100}
                              duration={600}
                              offset={-56}
                              data-testid='preview-side-navi-heading-link'
                              data-testid-e2e='preview-side-navi-heading-link'>
                              {childHeadingInfo.headingWord}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Card.Content>
    </Card>
  );
};

export default PreviewSideNavigation;
